"use client";
import { Fragment, useEffect, useState } from "react";
import { useRoomContext } from "@livekit/components-react";
import { LogLevel, RemoteTrackPublication, setLogLevel } from "livekit-client";

// @ts-expect-error: tinykeys may not have types, but we want to import it for hotkey support
import { tinykeys } from "tinykeys";

export const useDebugMode = ({ logLevel }: { logLevel?: LogLevel }): void => {
  const room = useRoomContext();

  useEffect(() => {
    setLogLevel(logLevel ?? "debug");

    // @ts-expect-error: Expose room context for debugging in browser console
    window.__lk_room = room;

    return () => {
      // @ts-expect-error: Remove room context from window on cleanup
      window.__lk_room = undefined;
    };
  }, [room, logLevel]);
};

/**
 * DebugMode - Displays room info, local/remote participants, and tracks.
 * Toggle with Shift+D.
 */
export const DebugMode = ({ logLevel }: { logLevel?: LogLevel }) => {
  const room = useRoomContext();
  const [isOpen, setIsOpen] = useState(false);
  const [, setRender] = useState({});
  const [roomSid, setRoomSid] = useState("");

  useEffect(() => {
    room.getSid().then(setRoomSid);
  }, [room]);

  useDebugMode({ logLevel });

  /**
   * Hotkey listener.
   * Toggles debug panel visibility on Shift+D.
   */
  useEffect(() => {
    if (typeof window === "undefined") return;

    const unsubscribe = tinykeys(window, {
      "Shift+D": () => {
        setIsOpen((open) => !open);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  /**
   * Auto-refresh interval.
   * Forces re-render every second to update dynamic stats when open.
   */
  useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(() => {
      setRender({});
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [isOpen]);

  if (typeof window === "undefined" || !isOpen) {
    return null;
  }

  const lp = room.localParticipant;

  if (!isOpen) {
    return <></>;
  } else {
    return (
      <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-80 z-50 overflow-auto p-8 text-white text-sm font-mono">
        <section id="room-info">
          <h3>
            Room Info {room.name}: {roomSid}
          </h3>
        </section>
        <details open>
          <summary>
            <b>Local Participant: {lp.identity}</b>
          </summary>
          <details open className="mb-4 p-2 border border-gray-700 rounded bg-gray-900">
            <summary>
              <b>Published tracks</b>
            </summary>
            <div>
              {Array.from(lp.trackPublications.values()).map((t) => (
                <Fragment key={t.trackSid}>
                  <div>
                    <i>
                      {t.source.toString()}
                      &nbsp;<span>{t.trackSid}</span>
                    </i>
                  </div>
                  <table>
                    <tbody>
                      <tr>
                        <td>Kind</td>
                        <td>
                          {t.kind}&nbsp;
                          {t.kind === "video" && (
                            <span>
                              {t.track?.dimensions?.width}x{t.track?.dimensions?.height}
                            </span>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td>Bitrate</td>
                        <td>{Math.ceil(t.track!.currentBitrate / 1000)} kbps</td>
                      </tr>
                    </tbody>
                  </table>
                </Fragment>
              ))}
            </div>
          </details>
          <details open className="mb-4 p-2 border border-gray-700 rounded bg-gray-900">
            <summary>
              <b>Permissions</b>
            </summary>
            <div>
              <table>
                <tbody>
                  {lp.permissions &&
                    Object.entries(lp.permissions).map(([key, val]) => (
                      <tr key={key}>
                        <td>{key}</td>
                        {key !== "canPublishSources" ? <td>{val.toString()}</td> : <td> {val.join(", ")} </td>}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </details>
        </details>

        <details>
          <summary>
            <b>Remote Participants</b>
          </summary>
          {Array.from(room.remoteParticipants.values()).map((p) => (
            <details key={p.sid} className="mb-4 p-2 border border-gray-700 rounded bg-gray-900">
              <summary>
                <b>
                  {p.identity}
                  <span></span>
                </b>
              </summary>
              <div>
                {Array.from(p.trackPublications.values()).map((t) => (
                  <Fragment key={t.trackSid}>
                    <div>
                      <i>
                        {t.source.toString()}
                        &nbsp;<span>{t.trackSid}</span>
                      </i>
                    </div>
                    <table>
                      <tbody>
                        <tr>
                          <td>Kind</td>
                          <td>
                            {t.kind}&nbsp;
                            {t.kind === "video" && (
                              <span>
                                {t.dimensions?.width}x{t.dimensions?.height}
                              </span>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td>Status</td>
                          <td>{trackStatus(t)}</td>
                        </tr>
                        {t.track && (
                          <tr>
                            <td>Bitrate</td>
                            <td>{Math.ceil(t.track.currentBitrate / 1000)} kbps</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </Fragment>
                ))}
              </div>
            </details>
          ))}
        </details>
      </div>
    );
  }
};

const trackStatus = (t: RemoteTrackPublication): string => {
  if (t.isSubscribed) {
    return t.isEnabled ? "enabled" : "disabled";
  } else {
    return "unsubscribed";
  }
};
